import { useParams } from "react-router";
import { format } from "date-fns";
import { ReloadIcon } from "vega-icons";

import { useGetDealTrackingForGp } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { SegmentedTabs } from "@vega-core/components/SegmentedTabs/SegmentedTabs";
import { m } from "@vega-core/paraglide/messages";
import { isGPCoInvestPortal } from "@vega-core/utils/global";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CommonPaths } from "@vega-modules/paths";
import { TrackViewType } from "@vega-modules/scenes/CoInvestmentsGP/constants/trackPageConstants";

import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { useDealCurrency } from "../hooks/useDealCurrency";
import { useDealName } from "../hooks/useDealName";
import { useTrackView } from "../hooks/useTrackView";
import { AllocationView } from "../tabs/AllocationView";
import { AnalyticsView } from "../tabs/AnalyticsView";
import { ProcessView } from "../tabs/ProcessView";

export const TrackPage = () => {
  useFeatureFlagGuard(
    "gp_co_investment",
    isGPCoInvestPortal ? undefined : CommonPaths.Home,
  );

  const { id: dealId = "" } = useParams<{ id: string }>();
  const { activeView, setActiveView } = useTrackView();

  const { currency: dealCurrency } = useDealCurrency(dealId);
  const { dealName, isLoading: isDealNameLoading } = useDealName(dealId);

  const {
    data: trackingResponse,
    dataUpdatedAt: trackingDataUpdatedAt,
    isPending: isTrackingDataPending,
    isFetching: isTrackingDataFetching,
    refetch: refetchTrackingData,
  } = useGetDealTrackingForGp(dealId, {
    query: {
      enabled: !!dealId,
      select: (data) => {
        const { data: trackingData } = data;

        if (!trackingData) {
          return undefined;
        }

        return trackingData;
      },
    },
  });

  const lastUpdated = trackingDataUpdatedAt
    ? format(new Date(trackingDataUpdatedAt), "PPpp")
    : null;

  const limitedPartnerBreakdown =
    trackingResponse?.limitedPartnerBreakdown ?? [];

  const renderActiveView = () => {
    if (activeView === TrackViewType.Process) {
      return (
        <ProcessView
          dealId={dealId}
          isLoading={isTrackingDataPending}
          limitedPartnerBreakdown={limitedPartnerBreakdown}
        />
      );
    }

    if (activeView === TrackViewType.Allocation) {
      return (
        <AllocationView
          dealCurrency={dealCurrency}
          dealId={dealId}
          fundContributionAmount={trackingResponse?.fundContributionAmount}
          isLoading={isTrackingDataPending}
          limitedPartnerBreakdown={limitedPartnerBreakdown}
          metrics={trackingResponse?.allocationSummary?.metrics ?? []}
        />
      );
    }

    return (
      <AnalyticsView
        dealId={dealId}
        isLoading={isTrackingDataPending}
        limitedPartnerBreakdown={limitedPartnerBreakdown}
        metrics={trackingResponse?.overview.metrics ?? []}
        subtotalsByStage={trackingResponse?.subtotalsByStage ?? []}
      />
    );
  };

  const tabs = [
    {
      id: TrackViewType.Process,
      label: m.coInvestments_track_view_process(),
    },
    {
      id: TrackViewType.Allocation,
      label: m.coInvestments_track_view_allocation(),
    },
    {
      id: TrackViewType.Analytics,
      label: m.coInvestments_track_view_analytics(),
    },
  ];

  return (
    <div className="mt-1">
      <CoInvestmentPageHeader
        action={
          <div className="flex items-center gap-2">
            {lastUpdated ? (
              <p
                className="body-small-regular text-text/secondary"
                data-testid="tracking-last-updated"
              >
                {m.coInvestments_tracking_lastUpdated({
                  timestamp: lastUpdated,
                })}
              </p>
            ) : null}
            <ThemedButton
              dataTestId="tracking-refresh-button"
              disabled={isTrackingDataFetching}
              icon={
                <ReloadIcon
                  className={
                    isTrackingDataFetching ? "animate-spin" : undefined
                  }
                />
              }
              onClick={refetchTrackingData}
              variant="secondary"
            >
              {m.btn_refresh()}
            </ThemedButton>
          </div>
        }
        fundName={dealName}
        isLoading={isDealNameLoading}
        showClosedDealNotice
        title={m.coInvestments_nav_track()}
      />

      <div className="mt-6 space-y-6">
        <SegmentedTabs
          activeTab={activeView}
          setActiveTab={setActiveView}
          tabs={tabs}
        />

        {renderActiveView()}
      </div>
    </div>
  );
};
